import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { EASE } from "./Reveal";

const lines = ["Technology", "that Empowers", "Humanity."];

const MaskedLine = ({ text, i, accent }) => (
  <span className="block overflow-hidden pb-3 -mb-3">
    <motion.span
      className={`block ${accent ? "text-[#7F9F43]" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section id="home" ref={ref} className="relative pt-36 lg:pt-44 pb-24 lg:pb-32 overflow-hidden" data-testid="hero-section">
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#7F9F43]/[0.06] blur-3xl pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-8 items-end">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="text-lg sm:text-2xl tracking-[0.2em] uppercase text-gray-400 font-semibold mb-8"
              data-testid="hero-eyebrow"
            >
              Huminence Technologies Inc.
            </motion.p>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-[#111827]"
              data-testid="hero-headline"
            >
              {lines.map((l, i) => (
                <MaskedLine key={l} text={l} i={i} accent={i === 2} />
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
              className="mt-8 max-w-xl text-base md:text-lg text-gray-500 leading-relaxed"
              data-testid="hero-subheading"
            >
              We build AI-powered products that preserve dignity, promote independence, and
              strengthen the human connection. We believe technology should not replace
              people — <span className="font-serif-italic text-[#111827] text-[1.1em]">it should empower them.</span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-cta-demo"
                className="inline-flex items-center rounded-full bg-[#7F9F43] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#6e8b39] hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
              >
                Request a Demo
              </a>
              <a
                href="#romii"
                data-testid="hero-cta-romii"
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-7 py-3.5 text-sm font-semibold text-[#111827] hover:border-[#7F9F43] hover:text-[#7F9F43] transition-colors duration-300"
              >
                Learn About Romii
                <ArrowDown size={15} />
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
              className="relative rounded-3xl overflow-hidden card-soft aspect-[4/5] lg:aspect-[4/4.6]"
              data-testid="hero-image"
            >
              <motion.img
                src="/assets/hero-warm.jpg"
                alt="A mother and daughter sharing a joyful moment at home"
                className="w-full h-full object-cover"
                style={{ y: imgY, scale: imgScale }}
              />
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl backdrop-blur-xl bg-white/75 px-5 py-4">
                <p className="text-xs tracking-[0.2em] uppercase text-[#7F9F43] font-bold">Our motive</p>
                <p className="text-sm text-gray-700 mt-1">
                  Keeping people connected, confident, and independent.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
